import { useCallback, useEffect, useMemo, useState } from "react"
import { uniq } from "lodash"

import AssetService from "services/AssetService"
import toastMessages from "stores/toastMessages"
import { Guid, IAppliedFilter, IMultipleEntities, Lmid } from "@/types"
import { collectFilterProps } from "utils/filters"
import AssetMaintenance, { IGetAssetMaintenantcesParams } from "models/AssetMaintenance"
import ArticleService from "services/ArticleService"

export interface IUseAssetMaintenancesParams {
    filters?: IAppliedFilter[]
    assetId?: Guid
    includeCompleted?: boolean
    pageSize?: number
    fillInventoryData?: boolean
}

interface IAssetMaintenance {
    maintenance: AssetMaintenance
    articleDescription?: string
    assetLmid?: Lmid
    assetOperationHours?: number
    assetLabeled?: boolean
}

interface IAssetCache {
    lmid: Lmid
    operationHours: number | null
    isLabeled: boolean
}

export default function useAssetMaintenances({
    filters,
    assetId,
    includeCompleted,
    pageSize = 20,
    fillInventoryData,
} : IUseAssetMaintenancesParams) {
    const [maintenances, setMaintenances] = useState<IAssetMaintenance[] | null>(null)
    const [isLoadingMaintenances, setIsLoadingMaintenances] = useState(false)
    const [isLoadingArticleAssets, setIsLoadingArticleAssets] = useState(false)
    const [articleDescriptions, setArticleDescriptions] = useState<Record<Guid, string>>({})
    const [assets, setAssets] = useState<Record<Guid, IAssetCache>>({})
    const [loadMoreCall, setLoadMoreCall] = useState<(() => Promise<IMultipleEntities<AssetMaintenance>>) | null>(null)

    const maintenancesParams = useMemo<IGetAssetMaintenantcesParams>(() => {
        setMaintenances(null)
        return {
            pageSize,
            ...filters ? collectFilterProps(filters) : {},
            includeCompleted,
            assetId,
        }
    }, [filters, pageSize, includeCompleted, assetId])

    const onFetched = useCallback(async (data: IMultipleEntities<AssetMaintenance>, isFirstPage?: boolean) => {
        const items = data.list || []
        if (items.length === 0) {
            if (isFirstPage) {
                setMaintenances([])
            }
            return
        }

        if (!fillInventoryData) {
            setMaintenances([
                ...(isFirstPage ? [] : (maintenances || [])),
                ...items.map(m => ({
                    maintenance: m,
                }))
            ])
            return
        }

        setIsLoadingArticleAssets(true)
        try {
            const assetIdsToFetch = uniq(
                items
                    .filter(m => !!m.assetId && !assets[m.assetId])
                    .map(m => m.assetId)
            )
            const articlesIdsToFetch = uniq(
                items
                    .filter(m => !m.assetId && !articleDescriptions[m.articleId])
                    .map(m => m.articleId)
            )

            const [assetArticles, articleAssets] = await Promise.all([
                assetIdsToFetch.length > 0 ? (await ArticleService.getAssetArticles({
                    assetIds: assetIdsToFetch,
                    pageSize: assetIdsToFetch.length,
                })).list : undefined,
                articlesIdsToFetch.length > 0 ? (await ArticleService.getArticleAssets({
                    articleIds: articlesIdsToFetch,
                    pageSize: articlesIdsToFetch.length,
                }))?.list : undefined,
            ])

            const newArticleDescriptions: Record<Guid, string> = {
                ...articleDescriptions,
                ...assetArticles?.reduce((acc, assetArticle) => {
                    acc[assetArticle.articleId] = assetArticle.articleDescription
                    return acc
                }, {}),
                ...articleAssets?.reduce((acc, articleAsset) => {
                    acc[articleAsset.id] = articleAsset.description
                    return acc
                }, {}),
            }
            setArticleDescriptions(newArticleDescriptions)

            const newAssets: Record<Guid, IAssetCache> = {
                ...assets,
                ...assetArticles?.reduce((acc, assetArticle) => {
                    acc[assetArticle.assetId] = {
                        lmid: assetArticle.lmid,
                        operationHours: assetArticle.operationHours,
                        isLabeled: assetArticle.isLabeled,
                    }
                    return acc
                }, {}),
            }
            setAssets(newAssets)

            const currentMaintenances: IAssetMaintenance[] = data.list.map(maintenance => ({
                maintenance,
                articleDescription: newArticleDescriptions[maintenance.articleId],
                assetLmid: newAssets[maintenance.assetId]?.lmid,
                assetOperationHours: newAssets[maintenance.assetId]?.operationHours ?? undefined,
                assetLabeled: newAssets[maintenance.assetId]?.isLabeled,
            }))

            setMaintenances(
                isFirstPage
                    ? currentMaintenances
                    : [...maintenances || [], ...currentMaintenances]
            )
        } catch (e) {
            toastMessages.showSystemError()
        }
        setIsLoadingArticleAssets(false)
    }, [articleDescriptions, assets, fillInventoryData, maintenances])


    const initFetchMaintenances = useCallback(async ({ initPageSize }: { initPageSize?: number } = {}) => {
        setLoadMoreCall(null)
        setIsLoadingMaintenances(true)

        try {
            const maintenancesResp = await AssetService.getAssetMaintenances(typeof initPageSize === "number"
                ? { ...maintenancesParams, pageSize: initPageSize }
                : maintenancesParams
            )
            setIsLoadingMaintenances(false)
            await onFetched(maintenancesResp, true)
            const nextPageNumber = maintenancesResp.list.length / maintenancesParams.pageSize! + 1
            setLoadMoreCall(maintenancesResp.loadMore ? () => () => AssetService.getAssetMaintenances({ ...maintenancesParams, pageNumber: nextPageNumber }) : null)
        } catch (e) {
            toastMessages.showSystemError()
        }
    }, [maintenancesParams, onFetched])


    useEffect(() => {
        initFetchMaintenances()
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [maintenancesParams])

    async function loadNextPage() {
        setLoadMoreCall(null)
        if (!loadMoreCall) return
        setIsLoadingMaintenances(true)
        const moreMaintenances = await loadMoreCall()
        setIsLoadingMaintenances(false)
        await onFetched(moreMaintenances)
        const nextPageNumber = (moreMaintenances?.pagination?.currentPage || 1) + 1
        setLoadMoreCall(moreMaintenances?.loadMore ? () => () => AssetService.getAssetMaintenances({ ...maintenancesParams, pageNumber: nextPageNumber }) : null)
    }

    async function refetchMaintenances() {
        if (!maintenances) return
        await initFetchMaintenances({ initPageSize: Math.ceil(maintenances.length / pageSize) * pageSize })
    }

    return {
        maintenances,
        loadNextPage,
        isLoading: isLoadingMaintenances || isLoadingArticleAssets,
        hasMore: !!loadMoreCall,
        refetchMaintenances,
    }
}
